import {
  IntervalKey,
  PeriodKey,
  DrawingToolKey,
  CandlestickColors,
  LineConfig,
  DrawingToolConfig,
  ViewState,
  IntervalSection,
  TAIndicatorDef,
  TAItem,
} from './types';
import { COLORS } from './colorTokens';

export const PERIOD_ORDER: PeriodKey[] = [
  '1D',
  '2D',
  '5D',
  'MTD',
  '1M',
  '3M',
  '6M',
  'YTD',
  '1Y',
  '2Y',
  '3Y',
  '5Y',
  '10Y',
];

export const IntervalConfig: Record<
  IntervalKey,
  { label: string; maxPeriod: PeriodKey }
> = {
  '1': { label: '1 min', maxPeriod: '1D' },
  '5': { label: '5 min', maxPeriod: '2D' },
  '15': { label: '15 min', maxPeriod: '5D' },
  '60': { label: '1 hr', maxPeriod: '5D' },
  d: { label: 'Daily', maxPeriod: '2Y' },
  w: { label: 'Weekly', maxPeriod: '5Y' },
  m: { label: 'Monthly', maxPeriod: '10Y' },
};

export const INTRADAY_INTERVALS: { key: IntervalKey; label: string }[] = [
  { key: '1', label: '1 minutes' },
  { key: '5', label: '5 minutes' },
  { key: '15', label: '15 minutes' },
  { key: '60', label: '1 hour' },
];

export const EXPANDABLE_INTERVALS: IntervalSection[] = [
  {
    key: 'd',
    label: 'Daily',
    periods: ['MTD', '1M', '2M', '3M', '6M', 'YTD', '1Y', '2Y'],
  },
  {
    key: 'w',
    label: 'Weekly',
    periods: ['1M', '2M', '3M', '6M', 'YTD', '1Y', '2Y', '3Y', '4Y', '5Y'],
  },
  {
    key: 'm',
    label: 'Monthly',
    periods: ['6M', 'YTD', '1Y', '2Y', '3Y', '4Y', '5Y', '10Y'],
  },
];

export const DEFAULT_PERIODS: Partial<Record<IntervalKey, PeriodKey>> = {
  d: '6M',
  w: '1Y',
  m: '5Y',
};

export const DrawingToolsList: { key: DrawingToolKey; label: string }[] = [
  { key: 'line', label: 'Line' },
  { key: 'retracement', label: 'Retracement' },
  { key: 'fans', label: 'Fans' },
  { key: 'arcs', label: 'Arcs' },
];

export const TA_CATALOGUE: Record<string, TAIndicatorDef[]> = {
  Trend: [
    { key: 'SMA', label: 'Simple Moving Average (SMA)' },
    { key: 'WMA', label: 'Weighted Moving Average (WMA)' },
    { key: 'EMA', label: 'Exponential Moving Average (EMA)' },
    { key: 'MEMA', label: 'Modified Exponential Moving Average (MEMA)' },
    { key: 'TVMA', label: 'Turnover Moving Average (TVMA)' },
    { key: 'ICH', label: 'Ichimoku Cloud (ICH)' },
    { key: 'MACD', label: 'MA Convergence Divergence (MACD)' },
    { key: 'LRT', label: 'Linear Regression Trend (LRT)' },
    { key: 'SAR', label: 'Parabolic SAR (SAR)' },
    { key: 'ADXR', label: 'Average DMI Rating (ADX-R)' },
    { key: 'DMI', label: 'Directional Movement Index (DMI)' },
    { key: 'BIAS', label: 'Bias' },
    { key: 'ALF', label: "Alexander's Filter (ALF)" },
  ],
  Volatility: [
    { key: 'BB', label: 'Bollinger Band (BB)' },
    { key: 'VC', label: "Chaikin's Volatility (VC)" },
    { key: 'ATR', label: 'Average True Range (ATR)' },
    { key: 'SD', label: 'Standard Deviation (SD)' },
    { key: 'VltyCC', label: 'Historical Volatility (VltyCC)' },
  ],
  Oscillator: [
    { key: 'KRI', label: 'Kairi Relative Index (KRI)' },
    { key: 'PL', label: 'Psychological Line (PL)' },
    { key: 'CO', label: "Chaikin's Oscillator (CO)" },
    { key: 'RCI', label: 'Rank Correlation Index (RCI)' },
    { key: 'FKD', label: 'Fast Stochastics (FKD)' },
    { key: 'SKD', label: 'Slow Stochastics (SKD)' },
    { key: 'CCI', label: 'Commodity Channel Index (CCI)' },
    { key: 'MI', label: 'Mass Index (MI)' },
    { key: 'UO', label: 'Ultimate Oscillator (UO)' },
    { key: 'Vector', label: 'Vector' },
    { key: 'DPO', label: 'Detrended Price Oscillator (DPO)' },
    { key: 'WAD', label: 'Williams A/D (WAD)' },
    { key: 'Trix', label: 'Trix' },
  ],
  Volume: [
    { key: 'Volume', label: 'Volume' },
    { key: 'VE', label: 'Volume Envelope (VE)' },
    { key: 'VAP', label: 'Volume at Price (VAP)' },
    { key: 'VOSC', label: 'Volume Oscillator (VOSC)' },
    { key: 'VROC', label: 'Volume Rate of Change (VROC)' },
    { key: 'ACD', label: 'Accumulation/Distribution (ACD)' },
    { key: 'OBV', label: 'On Balance Volume (OBV)' },
    { key: 'EOM', label: 'Ease of Movement (EOM)' },
    { key: 'FI', label: 'Force Index (FI)' },
    { key: 'MFI', label: 'Money Flow Index (MFI)' },
    { key: 'NVI', label: 'Negative Volume Index (NVI)' },
    { key: 'VR', label: 'Volume Ratio (VR)' },
  ],
  Momentum: [
    { key: 'ENV', label: 'Envelope (ENV)' },
    { key: 'MOM', label: 'Momentum (MOM)' },
    { key: 'RMI', label: 'Relative Momentum Index (RMI)' },
    { key: 'RSI', label: 'Relative Strength Index (RSI)' },
    { key: 'WR', label: 'Williams %R (WR)' },
  ],
  Price: [
    { key: 'POSC', label: 'Price Oscillator (POSC)' },
    { key: 'PROC', label: 'Price Rate of Change (PROC)' },
    { key: 'PRR', label: 'Price Range Ratio (PRR)' },
    { key: 'VWAP', label: 'VWAP' },
    { key: 'WC', label: 'Weighted Close (WC)' },
    { key: 'FIB', label: 'Fibonacci Lines (FIB)' },
    { key: 'PCV', label: 'Price Volatility (PCV)' },
    { key: 'RHI', label: 'Shinohara Ratio (RHI)' },
  ],
  Other: [
    { key: 'TP', label: 'Turning Point (TP)' },
    { key: 'RC', label: 'Ratio Cator (RC)' },
    { key: 'Turnover', label: 'Turnover' },
    { key: 'CC', label: 'Coppock Curve (CC)' },
    { key: 'KC', label: 'Keltner Channels (KC)' },
    { key: 'UI', label: 'Ulcer Index (UI)' },
  ],
};

export const MAX_TA = 5;
export const MAX_COMPARISON = 9;

export const COMPARISON_COLORS = [
  COLORS.chartCompare1,
  COLORS.chartCompare2,
  COLORS.chartCompare3,
  COLORS.chartCompare4,
  COLORS.chartCompare5,
  COLORS.chartCompare6,
  COLORS.chartCompare7,
  COLORS.chartCompare8,
];

export const TA_COLORS = [
  COLORS.chartTa1,
  COLORS.chartTa2,
  COLORS.chartTa3,
  COLORS.chartTa4,
  COLORS.chartTa5,
  COLORS.chartTa6,
  COLORS.chartTa7,
  COLORS.chartTa8,
];

export const DEFAULT_VIEW: ViewState = {
  showMainChart: true,
  showTA: true,
  showTrendlines: true,
  showTracker: true,
  showCrosshair: false,
  showCrosshairTooltip: false,
  showTimelineScroller: true,
};

export const DEFAULT_CANDLESTICK_COLORS: CandlestickColors = {
  upLine: COLORS.textUpColor,
  upBody: COLORS.textUpColor,
  downLine: COLORS.textDownColor,
  downBody: COLORS.textDownColor,
};

export const DEFAULT_LINE_CONFIG: LineConfig = {
  style: 's',
  thickness: 2,
  color: COLORS.assetCashDark,
};

export const DEFAULT_BAR_CONFIG: LineConfig = {
  style: 's',
  thickness: 1,
  color: COLORS.heatmapRange5,
};

export const DEFAULT_DRAWING_CONFIG: DrawingToolConfig = {
  color: COLORS.assetCashDark,
  thickness: 2,
  style: 's',
};
export const DEFAULT_ACTIVE_TA: TAItem[] = [
  {
    key: 'Volume',
    label: 'Volume',
    category: 'Volume',
    color: COLORS.textUpColor,
    period: 14,
    lines: [
      { enabled: true, period: 0, color: COLORS.textUpColor },
      { enabled: true, period: 0, color: COLORS.chartTa2 },
    ],
  },
];

export const MOCK_INDEXES = [
  {
    symbol: 'XAO',
    ric: '.AORD',
    name: 'All Ordinaries',
    assetType: 'index' as const,
    countryCode: 'AUS',
  },
  {
    symbol: 'XTL',
    ric: '.ATLI',
    name: 'S&P/ASX 20',
    assetType: 'index' as const,
    countryCode: 'AUS',
  },
  {
    symbol: 'XFL',
    ric: '.AFLI',
    name: 'S&P/ASX 50',
    assetType: 'index' as const,
    countryCode: 'AUS',
  },
  {
    symbol: 'XTO',
    ric: '.ATOI',
    name: 'S&P/ASX 100',
    assetType: 'index' as const,
    countryCode: 'AUS',
  },
  {
    symbol: 'XJO',
    ric: '.AXJO',
    name: 'S&P/ASX 200',
    assetType: 'index' as const,
    countryCode: 'AUS',
  },
  {
    symbol: 'DJI',
    ric: '.DJI',
    name: 'Dow Jones Index',
    assetType: 'index' as const,
    countryCode: 'USA',
  },
  {
    symbol: 'IXIC',
    ric: '.IXIC',
    name: 'Nasdaq Composite',
    assetType: 'index' as const,
    countryCode: 'USA',
  },
  {
    symbol: 'NDX',
    ric: '.NDX',
    name: 'NASDAQ 100',
    assetType: 'index' as const,
    countryCode: 'USA',
  },
  {
    symbol: 'SPX',
    ric: '.SPX',
    name: 'S&P 500 Index',
    assetType: 'index' as const,
    countryCode: 'USA',
  },
  {
    symbol: 'FTSE',
    ric: '.FTSE',
    name: 'FTSE 100',
    assetType: 'index' as const,
    countryCode: 'GBR',
  },
  {
    symbol: 'N225',
    ric: '.N225',
    name: 'Nikkei 225',
    assetType: 'index' as const,
    countryCode: 'JPN',
  },
];
